// Feb 2026 · Lightweight searchable multi-select dropdown.
//
// Why custom?
// The shadcn <Select> is single-value and renders every option in the
// DOM up-front, which was the biggest contributor to the slow Meetings
// filters UX (hundreds of projects → janky open).  This component:
//
//   • sorts the list once alphabetically (memoised)
//   • lazy-filters by free-text search (memoised, bounded to 300 rows)
//   • chip-free compact trigger (just a count) so the toolbar stays tidy
//   • "Select all" / "Clear" shortcuts
//   • pointer + keyboard accessible, Esc closes, Enter toggles
//   • works equally well on tiny lists (clients) or huge lists (projects)
//
// Props:
//   options     [{ value, label, hint? }]  — unsorted ok, we sort
//   value       string[]                   — selected values
//   onChange(array)                        — called with new selected array
//   placeholder string                     — shown when nothing selected
//   label       string                     — a11y label used for aria-label
//   width       tailwind class e.g. "w-44" — trigger width
//   testid      string                     — base data-testid for the control
//   disabled    bool
import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search as SearchIcon, X, Check } from "lucide-react";

const MAX_RENDER = 300; // Hard cap on rendered rows to keep open fast.

export default function FastMultiSelect({
  options = [],
  value = [],
  onChange,
  placeholder = "All",
  label,
  width = "w-44",
  testid = "fms",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const btnRef = useRef(null);
  const popRef = useRef(null);

  // Sort options once (per options reference). Case-insensitive by label.
  const sorted = useMemo(() => {
    const copy = Array.isArray(options) ? [...options] : [];
    copy.sort((a, b) => (a.label || "").toLowerCase().localeCompare((b.label || "").toLowerCase()));
    return copy;
  }, [options]);

  // Filter by search (bounded to MAX_RENDER so the paint stays cheap even
  // when the user has 2000 projects in their workspace).
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return sorted.slice(0, MAX_RENDER);
    const out = [];
    for (const o of sorted) {
      if ((o.label || "").toLowerCase().includes(term)) {
        out.push(o);
        if (out.length >= MAX_RENDER) break;
      }
    }
    return out;
  }, [sorted, q]);

  // Lookup selected set for O(1) toggling + chip rendering.
  const selectedSet = useMemo(() => new Set(value || []), [value]);
  const selectedLabels = useMemo(() => {
    const m = new Map(sorted.map((o) => [o.value, o.label]));
    return (value || []).map((v) => m.get(v) || v);
  }, [sorted, value]);

  // Close on outside click + Esc.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (!popRef.current || !btnRef.current) return;
      if (popRef.current.contains(e.target) || btnRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggle = (val) => {
    if (!onChange) return;
    if (selectedSet.has(val)) {
      onChange((value || []).filter((v) => v !== val));
    } else {
      onChange([...(value || []), val]);
    }
  };

  const selectAllVisible = () => {
    if (!onChange) return;
    const vals = filtered.map((o) => o.value);
    const merged = Array.from(new Set([...(value || []), ...vals]));
    onChange(merged);
  };

  const clearAll = () => { if (onChange) onChange([]); };

  const count = (value || []).length;
  const triggerLabel = count === 0
    ? placeholder
    : count === 1
      ? selectedLabels[0]
      : `${count} selected`;

  return (
    <div className="relative inline-block">
      <button
        ref={btnRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label || placeholder}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={`${width} h-8 flex items-center justify-between gap-1 border border-input bg-background px-2 text-xs rounded-none hover:bg-muted ${count ? "border-primary text-primary bg-primary/5" : "text-foreground/90"} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        data-testid={`${testid}-trigger`}
      >
        <span className="truncate text-left flex-1">{triggerLabel}</span>
        {count > 0 && (
          <span
            role="button"
            aria-label="Clear selection"
            tabIndex={-1}
            onClick={(e) => { e.stopPropagation(); clearAll(); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); clearAll(); } }}
            className="shrink-0 text-foreground/60 hover:text-foreground inline-flex items-center justify-center"
            data-testid={`${testid}-clear`}
          >
            <X className="w-3 h-3" />
          </span>
        )}
        <ChevronDown className="w-3 h-3 shrink-0 opacity-60" />
      </button>
      {open && (
        <div
          ref={popRef}
          className={`absolute z-50 mt-1 ${width} min-w-[14rem] max-w-[22rem] bg-background border border-border shadow-lg rounded-none`}
          data-testid={`${testid}-panel`}
        >
          <div className="flex items-center gap-1 border-b border-border px-2 py-1.5 bg-muted/40">
            <SearchIcon className="w-3.5 h-3.5 text-muted-foreground" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search…"
              className="flex-1 h-6 bg-transparent text-xs outline-none"
              data-testid={`${testid}-search`}
            />
            {q && (
              <button
                type="button"
                onClick={() => setQ("")}
                className="text-muted-foreground hover:text-foreground"
                data-testid={`${testid}-search-clear`}
                aria-label="Clear search"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
          <div className="flex items-center justify-between gap-2 px-2 py-1 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
            <button type="button"
                    onClick={selectAllVisible}
                    className="hover:text-foreground"
                    data-testid={`${testid}-select-all`}>
              Select {q ? "matching" : "all"}
            </button>
            <span>{count} selected</span>
            <button type="button"
                    onClick={clearAll}
                    className="hover:text-foreground"
                    data-testid={`${testid}-clear-all`}>
              Clear
            </button>
          </div>
          <ul role="listbox"
              aria-multiselectable="true"
              className="max-h-64 overflow-y-auto"
              data-testid={`${testid}-list`}>
            {filtered.length === 0 ? (
              <li className="px-2 py-2 text-xs text-muted-foreground italic text-center">No matches</li>
            ) : (
              filtered.map((o) => {
                const checked = selectedSet.has(o.value);
                return (
                  <li key={o.value} role="option" aria-selected={checked}>
                    <button
                      type="button"
                      onClick={() => toggle(o.value)}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 text-left text-xs hover:bg-muted ${checked ? "bg-primary/5" : ""}`}
                      data-testid={`${testid}-opt-${o.value}`}
                    >
                      <span className={`shrink-0 w-3.5 h-3.5 border ${checked ? "bg-primary border-primary text-primary-foreground" : "border-border"} inline-flex items-center justify-center`}>
                        {checked && <Check className="w-2.5 h-2.5" />}
                      </span>
                      <span className="truncate">{o.label}</span>
                      {o.hint && <span className="ml-auto text-[10px] text-muted-foreground shrink-0">{o.hint}</span>}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
          {sorted.length > filtered.length && !q && (
            <div className="px-2 py-1 text-[10px] text-muted-foreground italic border-t border-border">
              Showing first {MAX_RENDER} of {sorted.length}. Type to find more.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
